import React from 'react';
import { Outlet, Link, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  LayoutDashboard,
  Package,
  FolderTree,
  ShoppingCart,
  Users,
  Settings,
  Search,
  LogOut,
  Menu,
  X,
} from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useUIStore } from '../../store/store';
import toast from 'react-hot-toast';

const AdminLayout: React.FC = () => {
  const { isSidebarOpen, toggleSidebar } = useUIStore();
  const { signOut, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const menuItems = [
    { path: '/admin', icon: <LayoutDashboard className="w-5 h-5" />, label: 'Панель управления' },
    { path: '/admin/products', icon: <Package className="w-5 h-5" />, label: 'Товары' },
    { path: '/admin/categories', icon: <FolderTree className="w-5 h-5" />, label: 'Категории' },
    { path: '/admin/orders', icon: <ShoppingCart className="w-5 h-5" />, label: 'Заказы' },
    { path: '/admin/users', icon: <Users className="w-5 h-5" />, label: 'Пользователи' },
    { path: '/admin/seo', icon: <Search className="w-5 h-5" />, label: 'SEO' },
    { path: '/admin/settings', icon: <Settings className="w-5 h-5" />, label: 'Настройки' },
  ];

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/admin/login');
      toast.success('Вы успешно вышли из системы');
    } catch {
      toast.error('Ошибка при выходе из системы');
    }
  };

  // Redirect if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-40 w-64 h-screen transition-transform ${
          isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } bg-white border-r border-gray-200`}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <Link to="/admin" className="text-xl font-bold text-primary-600">
            Админ-панель
          </Link>
          <button onClick={toggleSidebar} className="p-2 rounded-lg lg:hidden hover:bg-gray-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-4 space-y-1">
          {menuItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center px-4 py-2 rounded-lg transition-colors ${
                location.pathname === item.path
                  ? 'bg-primary-50 text-primary-600'
                  : 'text-gray-700 hover:bg-gray-50'
              }`}
            >
              {item.icon}
              <span className="ml-3">{item.label}</span>
            </Link>
          ))}

          <button
            onClick={handleSignOut}
            className="flex items-center w-full px-4 py-2 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            <LogOut className="w-5 h-5" />
            <span className="ml-3">Выйти</span>
          </button>
        </nav>
      </aside>

      {/* Main content */}
      <div className={`p-4 ${isSidebarOpen ? 'lg:ml-64' : ''}`}>
        <div className="mb-4 flex items-center justify-between">
          <button onClick={toggleSidebar} className="p-2 rounded-lg lg:hidden hover:bg-gray-100">
            <Menu className="w-5 h-5" />
          </button>
        </div>

        <main className="p-4 bg-white rounded-lg shadow">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
